import { useState, useEffect } from 'react';
import {
    getGuestyWebhookMessages,
    getGuestyRawWebhookMessageById,
    getAllUsers,
    getGuestyFailedMessagesCount,
    toggleGuestyMessageStatus,
    convertMessageToCamelCase,
    filterRecentMessages,
    sortMessagesByTime,
    extractUniqueAddresses,
    extractUniqueReservationIds,
    extractStatusTypesAndCounts,
    createCustomerMap,
    createCustomersList,
    updateCustomersWithMessageCounts,
    applyTextSearchFilter,
    generateDateHighlighting
} from './guestyMessageService';

/**
 * Custom hook for managing Guesty message state and operations
 */
export const useGuestyMessages = () => {
    // State for messages and filtering
    const [messages, setMessages] = useState([]);
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    
    // Customer filter state
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [customerSearchText, setCustomerSearchText] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerMap, setCustomerMap] = useState({});
    
    // Address filter state
    const [addressSearchText, setAddressSearchText] = useState("");
    const [uniqueAddresses, setUniqueAddresses] = useState([]);
    const [filteredAddresses, setFilteredAddresses] = useState([]);
    const [showAddressDropdown, setShowAddressDropdown] = useState(false);
    
    // Reservation ID filter state
    const [reservationIdSearchText, setReservationIdSearchText] = useState("");
    const [uniqueReservationIds, setUniqueReservationIds] = useState([]);
    const [filteredReservationIds, setFilteredReservationIds] = useState([]);
    const [showReservationIdDropdown, setShowReservationIdDropdown] = useState(false);
    
    // Raw message modal state
    const [showRawMessage, setShowRawMessage] = useState(false);
    const [rawMessage, setRawMessage] = useState(null);
    const [rawMessageLoading, setRawMessageLoading] = useState(false);
    const [rawMessageError, setRawMessageError] = useState(null);
    
    // Status toggle state
    const [isToggling, setIsToggling] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    
    // Message status filter state
    const [selectedStatus, setSelectedStatus] = useState("");
    const [statusTypes, setStatusTypes] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    
    // Date highlighting state
    const [dateHighlightMap, setDateHighlightMap] = useState(new Map());

    // Initialize data on mount
    useEffect(() => {
        fetchMessages();
        fetchCustomers();
    }, []);
    
    // Update customer message counts, extract unique addresses, and status types when messages change
    useEffect(() => {
        if (messages.length > 0) {
            // Count messages per customer
            if (customers.length > 0) {
                const updatedCustomers = updateCustomersWithMessageCounts(customers, messages);
                
                // Use a custom comparison to prevent infinite loops
                const customersChanged = JSON.stringify(updatedCustomers) !== JSON.stringify(customers);
                if (customersChanged) {
                    setCustomers(updatedCustomers);
                }
            }
            
            // Extract unique addresses
            const uniqueAddressList = extractUniqueAddresses(messages);
            setUniqueAddresses(uniqueAddressList);
            setFilteredAddresses(uniqueAddressList);
            
            // Extract unique reservation IDs
            const uniqueReservationIdsList = extractUniqueReservationIds(messages);
            setUniqueReservationIds(uniqueReservationIdsList);
            setFilteredReservationIds(uniqueReservationIdsList);
            
            // Extract unique status types and count messages for each status
            const { statusTypes: statusList, statusCounts: statuses } = extractStatusTypesAndCounts(messages);
            setStatusTypes(statusList);
            setStatusCounts(statuses);
        }
    }, [messages, customers]);
    
    // Filter customers based on search text
    useEffect(() => {
        if (customerSearchText) {
            const filtered = customers.filter(customer => 
                customer.name.toLowerCase().includes(customerSearchText.toLowerCase())
            );
            setFilteredCustomers(filtered);
        } else {
            setFilteredCustomers(customers);
        }
    }, [customerSearchText, customers]);
    
    // Filter addresses based on search text
    useEffect(() => {
        if (addressSearchText) {
            const filtered = uniqueAddresses.filter(address => 
                address.toLowerCase().includes(addressSearchText.toLowerCase())
            );
            setFilteredAddresses(filtered);
        } else {
            setFilteredAddresses(uniqueAddresses);
        }
    }, [addressSearchText, uniqueAddresses]);

    // Filter reservation IDs based on search text
    useEffect(() => {
        if (reservationIdSearchText) {
            const filtered = uniqueReservationIds.filter(reservationId => 
                reservationId.toLowerCase().includes(reservationIdSearchText.toLowerCase())
            );
            setFilteredReservationIds(filtered);
        } else {
            setFilteredReservationIds(uniqueReservationIds);
        }
    }, [reservationIdSearchText, uniqueReservationIds]);

    const fetchCustomers = () => {
        getAllUsers()
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        const users = Array.isArray(result) ? result : result?.users || [];
                        const map = createCustomerMap(users);
                        const customersList = createCustomersList(users);
                        
                        setCustomerMap(map);
                        setCustomers(customersList);
                    });
                }
            })
            .catch(error => {
                console.log('Error fetching customers:', error);
            });
    };
    
    const fetchMessages = (customerId = null) => {
        setIsLoading(true);
        setError(null);
        
        getGuestyWebhookMessages(customerId)
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result.success && result.messages) {
                            // Convert snake_case to camelCase for each message
                            const camelCaseMessages = result.messages.map(convertMessageToCamelCase);
                            
                            // Filter messages from the last 2 months
                            const recentMessages = filterRecentMessages(camelCaseMessages);
                            
                            // Sort messages by receivedTime (latest first)
                            const sortedMessages = sortMessagesByTime(recentMessages);
                            
                            // Store all messages
                            setMessages(sortedMessages);
                            
                            // Generate date highlighting for same-day check-in/check-out pairs
                            const highlightMap = generateDateHighlighting(sortedMessages);
                            setDateHighlightMap(highlightMap);
                            
                            // Apply existing filters if they exist
                            let filteredResult = sortedMessages;
                            
                            // If customerId is provided, filter by customer
                            if (customerId) {
                                filteredResult = filteredResult.filter(message => 
                                    message.cosCustomerId === customerId
                                );
                            }
                            
                            // Apply status filter if one is selected
                            if (selectedStatus) {
                                filteredResult = filteredResult.filter(message => 
                                    message.processingStatus === selectedStatus
                                );
                            }
                            
                            // Apply address filter if one is selected
                            if (addressSearchText) {
                                filteredResult = filteredResult.filter(message => 
                                    message.listingAddress === addressSearchText
                                );
                            }
                            
                            // Apply reservation ID filter if one is selected
                            if (reservationIdSearchText) {
                                filteredResult = filteredResult.filter(message => 
                                    message.reservationId === reservationIdSearchText
                                );
                            }
                            
                            // Apply text search filter
                            if (searchText) {
                                filteredResult = applyTextSearchFilter(filteredResult, searchText, customerMap);
                            }
                            
                            setFilteredMessages(filteredResult);
                        } else {
                            setError(result.message || "Failed to load webhook messages");
                        }
                    });
                } else {
                    response.text().then(result => {
                        console.log(result);
                        setError("Failed to load webhook messages");
                    });
                }
            })
            .catch(error => {
                console.log('error', error);
                setError("An error occurred while fetching webhook messages");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    
    const fetchRawMessage = (id) => {
        setRawMessageLoading(true);
        setRawMessageError(null);
        setRawMessage(null);
        
        getGuestyRawWebhookMessageById(id)
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result.success && result.message) {
                            try {
                                // Convert snake_case to camelCase and parse the raw message
                                const rawMessageContent = result.message.raw_message || result.message.rawMessage;
                                const parsedMessage = JSON.parse(rawMessageContent);
                                
                                // Store the raw message with additional metadata
                                setRawMessage({
                                    message: parsedMessage,
                                    cosCustomerId: result.message.cos_customer_id || result.message.cosCustomerId
                                });
                            } catch (e) {
                                // If parsing fails, display as string
                                const rawMessageContent = result.message.raw_message || result.message.rawMessage;
                                setRawMessage({
                                    message: rawMessageContent,
                                    cosCustomerId: result.message.cos_customer_id || result.message.cosCustomerId
                                });
                                setRawMessageError("Could not parse JSON message");
                            }
                        } else {
                            setRawMessageError(result.message || "Failed to load raw message");
                        }
                    });
                } else {
                    response.text().then(result => {
                        console.log(result);
                        setRawMessageError("Failed to load raw message");
                    });
                }
            })
            .catch(error => {
                console.log('error', error);
                setRawMessageError("An error occurred while fetching raw message");
            })
            .finally(() => {
                setRawMessageLoading(false);
            });
    };
    
    const handleToggleStatus = (id) => {
        setIsToggling(true);
        setTogglingId(id);
        
        toggleGuestyMessageStatus(id)
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result && !result.error) {
                            console.log("Toggle result:", result);
                            setError(null); // Clear any previous errors

                            // Update the message status locally
                            const updateMessageStatus = (prevMessages) => {
                                return prevMessages.map(msg => {
                                    if (msg.id === id) {
                                        // Determine the new status
                                        const currentStatus = msg.processingStatus;
                                        let newStatus = currentStatus;
                                        if (currentStatus === 'FAILED' || currentStatus === 'ERROR') {
                                            newStatus = 'IGNORED';
                                        } else if (currentStatus === 'IGNORED') {
                                            newStatus = 'FAILED'; // Toggle back to FAILED
                                        }
                                        return { ...msg, processingStatus: newStatus };
                                    }
                                    return msg;
                                });
                            };

                            setMessages(updateMessageStatus);
                            setFilteredMessages(updateMessageStatus);
                            
                            // Refresh the failed messages count in the UI
                            fetchFailedMessagesCount();
                            
                        } else {
                            setError(result.error || "Failed to toggle message status");
                        }
                    });
                } else {
                    response.text().then(result => {
                        console.log(result);
                        setError("Failed to toggle message status");
                    });
                }
            })
            .catch(error => {
                console.log('error', error);
                setError("An error occurred while toggling message status");
            })
            .finally(() => {
                setIsToggling(false);
                setTogglingId(null);
            });
    };
    
    const fetchFailedMessagesCount = () => {
        getGuestyFailedMessagesCount()
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result && result.count !== undefined) {
                            // Update the count in App.js through window event
                            window.dispatchEvent(new CustomEvent('failedMessagesCountUpdated', {
                                detail: { count: result.count }
                            }));
                        }
                    });
                }
            })
            .catch(error => {
                console.error("Error fetching failed messages count:", error);
            });
    };
    
    const handleViewRawMessage = (id) => {
        setShowRawMessage(true);
        fetchRawMessage(id);
    };
    
    const handleCustomerFilterChange = (customerId) => {
        setSelectedCustomerId(customerId);
        setCustomerSearchText(customerId ? customerMap[customerId] || "" : "");
        
        if (customerId) {
            fetchMessages(customerId);
        } else {
            fetchMessages();
        }
    };
    
    const handleCustomerSearchChange = (e) => {
        const value = e.target.value;
        setCustomerSearchText(value);
        
        // Show dropdown when typing
        setShowCustomerDropdown(true);
        
        // Clear selected customer if search field is cleared
        if (!value) {
            setSelectedCustomerId("");
            fetchMessages();
        }
    };
    
    const handleStatusFilter = (status) => {
        setSelectedStatus(status);
        
        if (!status) {
            // If no status is selected, show all messages (respecting other filters)
            if (selectedCustomerId) {
                fetchMessages(selectedCustomerId);
            } else {
                fetchMessages();
            }
        } else {
            // Filter messages by the selected status
            const statusFiltered = messages.filter(message => message.processingStatus === status);
            setFilteredMessages(statusFiltered);
        }
    };
    
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        const filtered = applyTextSearchFilter(messages, value, customerMap);
        setFilteredMessages(filtered);
    };

    const refreshMessages = () => {
        // Keep the current filters and refresh data
        if (selectedCustomerId) {
            fetchMessages(selectedCustomerId);
        } else {
            fetchMessages();
        }
    };

    return {
        // State
        messages,
        filteredMessages,
        isLoading,
        error,
        searchText,
        selectedCustomerId,
        customerSearchText,
        filteredCustomers,
        showCustomerDropdown,
        customers,
        customerMap,
        addressSearchText,
        uniqueAddresses,
        filteredAddresses,
        showAddressDropdown,
        reservationIdSearchText,
        uniqueReservationIds,
        filteredReservationIds,
        showReservationIdDropdown,
        showRawMessage,
        rawMessage,
        rawMessageLoading,
        rawMessageError,
        isToggling,
        togglingId,
        selectedStatus,
        statusTypes,
        statusCounts,
        dateHighlightMap,
        
        // Setters
        setFilteredMessages,
        setAddressSearchText,
        setReservationIdSearchText,
        setShowCustomerDropdown,
        setShowAddressDropdown,
        setShowReservationIdDropdown,
        setShowRawMessage,
        
        // Handlers
        handleViewRawMessage,
        handleCustomerFilterChange,
        handleCustomerSearchChange,
        handleStatusFilter,
        handleSearchChange,
        handleToggleStatus,
        refreshMessages
    };
};
